import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicePageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:before {
        content: "✓";
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

const ImageSection = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
  }
`;

const ModificationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const ModificationCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  text-align: center;

  .icon {
    font-size: 3rem;
    color: var(--secondary-color);
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 20px;
    font-size: 0.9rem;
  }
`;

const CTASection = styled.div`
  background: var(--background-dark);
  color: white;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    margin-bottom: 30px;
    opacity: 0.9;
  }

  .btn {
    background: var(--secondary-color);
    color: white;
    padding: 15px 30px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(245, 115, 58, 0.3);
    }
  }
`;

const CustomModifications = () => {
  return (
    <ServicePageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Custom Modifications</PageTitle>
          <PageSubtitle>
            Transform your container with professional modifications. From
            simple additions to complete conversions - we bring your vision to
            life.
          </PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <ContentSection>
            <h2>Professional Container Modifications</h2>
            <p>
              Our expert team can customize your shipping container to meet any
              specific requirement. Whether you need additional access points,
              specialized storage solutions, or complete office conversions, we
              have the expertise to deliver.
            </p>
            <p>
              All modifications are performed by certified professionals using
              high-quality materials and maintaining the structural integrity of
              your container.
            </p>
            <ul>
              <li>Custom doors and windows installation</li>
              <li>Electrical and lighting systems</li>
              <li>Insulation and climate control</li>
              <li>Interior partitions and shelving</li>
              <li>Security features and locks</li>
              <li>Paint and branding options</li>
            </ul>
          </ContentSection>

          <ImageSection>
            <img
              src="/assets/2025/08/Image4.jpg"
              alt="Custom Modified Container"
            />
          </ImageSection>
        </ContentGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Popular Modifications
        </h2>

        <ModificationGrid>
          <ModificationCard>
            <div className="icon">
              <i className="fas fa-door-open"></i>
            </div>
            <h3>Roll-up Doors</h3>
            <p>
              Add convenient roll-up doors to the sides of your container for
              easy access and improved workflow.
            </p>
          </ModificationCard>

          <ModificationCard>
            <div className="icon">
              <i className="fas fa-window-maximize"></i>
            </div>
            <h3>Windows & Doors</h3>
            <p>
              Install windows, personnel doors, and emergency exits for office
              conversions and workspace applications.
            </p>
          </ModificationCard>

          <ModificationCard>
            <div className="icon">
              <i className="fas fa-bolt"></i>
            </div>
            <h3>Electrical Systems</h3>
            <p>
              Complete electrical installation including outlets, lighting, and
              panel boxes for powered applications.
            </p>
          </ModificationCard>

          <ModificationCard>
            <div className="icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <h3>Insulation</h3>
            <p>
              Professional insulation services for temperature control and
              energy efficiency in all climates.
            </p>
          </ModificationCard>

          <ModificationCard>
            <div className="icon">
              <i className="fas fa-paint-brush"></i>
            </div>
            <h3>Custom Paint</h3>
            <p>
              Professional painting services with color matching and corporate
              branding options available.
            </p>
          </ModificationCard>

          <ModificationCard>
            <div className="icon">
              <i className="fas fa-lock"></i>
            </div>
            <h3>Security Upgrades</h3>
            <p>
              Enhanced security features including specialized locks, security
              boxes, and reinforcement options.
            </p>
          </ModificationCard>
        </ModificationGrid>

        <CTASection>
          <h2>Ready to Customize Your Container?</h2>
          <p>
            Contact our modification specialists to discuss your project
            requirements and receive a detailed quote for your custom container
            solution.
          </p>
          <Link to="/contact" className="btn">
            Get Custom Quote
          </Link>
        </CTASection>
      </Container>
    </ServicePageWrapper>
  );
};

export default CustomModifications;
